import {
  CellClassParams,
  ICellRendererParams,
  IRowNode,
} from 'ag-grid-enterprise';
import dayjs from 'dayjs';
import get from 'lodash/get';

import { formatCurrency } from 'utils/helper';

import { LineItem } from 'types/reporting';
import { RevenueRecognitionColDef } from 'types/revenue/ColumnDef';

export function isAutoTotalRow<TData = unknown>(
  rowNode: IRowNode<TData> | undefined | null
) {
  if (!rowNode) return false;
  // Check for footer rows (AgGrid's built-in group totals) - these are the actual total rows
  if (rowNode.footer && rowNode.aggData) return true;
  // Original logic for other auto total rows
  return Boolean(rowNode.aggData && !rowNode.data && !rowNode.group);
}

function defineDataColumn(
  item: RevenueRecognitionColDef
): RevenueRecognitionColDef {
  // If there is a data path prefix, strip it
  const fieldNameDate = item.field?.includes('.')
    ? item.field.split('.')[1]
    : item.field;

  // Parse the date string as UTC to match backend keys
  const date = dayjs.utc(fieldNameDate);

  return {
    ...item,
    minWidth: 150,
    cellDataType: 'number',
    cellStyle: {
      textAlign: 'right',
      borderRight: '1px solid #E9E9E9',
    },
    aggFunc: 'sum',
    headerName: date.format('MMM YYYY'),
    cellRenderer: (params: ICellRendererParams<LineItem>) => {
      const field = params.colDef?.field;

      // Show aggregated totals for auto total rows (grand totals)
      if (isAutoTotalRow(params.node)) {
        return (
          <b>
            {formatCurrency({ value: params.value || 0, returnHyphen: true })}
          </b>
        );
      }

      // Show aggregated totals for group rows (not bold)
      if (params.node?.group) {
        return formatCurrency({
          value: params.value || 0,
          returnHyphen: true,
        });
      }

      return formatCurrency({
        value: get(params.node.data, field!, 0),
        returnHyphen: true,
      });
    },
    headerClass: ['ag-right-aligned-header', 'headerCell'],
    cellClass: (params: CellClassParams<LineItem>) => {
      const classes = ['dataCell'];

      const rowData = params.node?.data;

      if (rowData && rowData.Children && rowData.Children.length !== 0) {
        classes.push('groupCell', 'boldText');
      }

      return classes;
    },
  };
}

export function createMonthlyColumns(
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
  columnPrefix?: string
): RevenueRecognitionColDef[] {
  let columnDefs: RevenueRecognitionColDef[] = [];
  const columnKeys: string[] = [];

  let currentDate = startDate.clone();

  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
    columnKeys.push(currentDate.format('YYYY-MM-DDTHH:mm:ss'));

    const nextDate = currentDate.add(1, 'month').startOf('month');
    currentDate = nextDate;
  }

  columnDefs = columnKeys.map((key) => ({
    field: `${columnPrefix ? columnPrefix + '.' : ''}${key}`,
  }));

  return columnDefs.map(defineDataColumn);
}

export function createQuarterlyColumns(
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
  columnPrefix?: string
): RevenueRecognitionColDef[] {
  const columnDefs: RevenueRecognitionColDef[] = [];
  const startYear = startDate.year();
  const endYear = endDate.year();
  const startQuarter = startDate.quarter();
  const endQuarter = endDate.quarter();

  for (let year = startYear; year <= endYear; year++) {
    const quarterStart = year === startYear ? startQuarter : 1;
    const quarterEnd = year === endYear ? endQuarter : 4;

    for (let quarter = quarterStart; quarter <= quarterEnd; quarter++) {
      const quarterColumn: RevenueRecognitionColDef = {
        field: `quarter_${year}_Q${quarter}`,
        headerName: `Q${quarter} ${year}`,
        minWidth: 150,
        cellDataType: 'number',
        cellStyle: {
          textAlign: 'right',
          borderRight: '1px solid #E9E9E9',
        },
        aggFunc: 'sum',
        valueGetter: (params) => {
          const data = params.data as any;
          const monthlyData = data?.[columnPrefix || 'monthly_data'];
          if (!monthlyData) return 0;
          let total = 0;
          for (let m = (quarter - 1) * 3; m < quarter * 3; m++) {
            const key = dayjs
              .utc()
              .year(year)
              .month(m)
              .startOf('month')
              .format('YYYY-MM-DDTHH:mm:ss');
            total += monthlyData[key] || 0;
          }
          return total;
        },
        cellRenderer: (params: ICellRendererParams<LineItem>) => {
          if (isAutoTotalRow(params.node)) {
            return (
              <b>
                {formatCurrency({
                  value: params.value || 0,
                  returnHyphen: true,
                })}
              </b>
            );
          }
          return formatCurrency({
            value: params.value || 0,
            returnHyphen: true,
          });
        },
        headerClass: ['ag-right-aligned-header', 'headerCell'],
        cellClass: ['dataCell'],
      };

      columnDefs.push(quarterColumn);
    }
  }

  return columnDefs;
}

export function createYearlyColumns(
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
  columnPrefix?: string
): RevenueRecognitionColDef[] {
  const columnDefs: RevenueRecognitionColDef[] = [];
  const startYear = startDate.year();
  const endYear = endDate.year();

  for (let year = startYear; year <= endYear; year++) {
    const yearColumn: RevenueRecognitionColDef = {
      field: `year_${year}`,
      headerName: `${year}`,
      minWidth: 150,
      cellDataType: 'number',
      cellStyle: {
        textAlign: 'right',
        borderRight: '1px solid #E9E9E9',
      },
      aggFunc: 'sum',
      valueGetter: (params) => {
        const data = params.data as any;
        const monthlyData = data?.[columnPrefix || 'monthly_data'];
        if (!monthlyData) return 0;
        let total = 0;
        for (let m = 0; m < 12; m++) {
          const key = dayjs()
            .year(year)
            .month(m)
            .startOf('month')
            .format('YYYY-MM-DDTHH:mm:ss');
          total += monthlyData[key] || 0;
        }
        return total;
      },
      cellRenderer: (params: ICellRendererParams<LineItem>) => {
        if (isAutoTotalRow(params.node)) {
          return (
            <b>
              {formatCurrency({ value: params.value || 0, returnHyphen: true })}
            </b>
          );
        }
        return formatCurrency({
          value: params.value || 0,
          returnHyphen: true,
        });
      },
      headerClass: ['ag-right-aligned-header', 'headerCell'],
      cellClass: ['dataCell'],
    };

    columnDefs.push(yearColumn);
  }

  return columnDefs;
}

export function createPeriodicityColumns(
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
  periodicity: string[],
  columnPrefix?: string
): RevenueRecognitionColDef[] {
  // Get the first (and only) periodicity selection
  const selectedPeriodicity = periodicity[0] || 'monthly';

  // Return columns based on the single selected periodicity
  switch (selectedPeriodicity) {
    case 'quarterly':
      return createQuarterlyColumns(startDate, endDate, columnPrefix);
    case 'yearly':
      return createYearlyColumns(startDate, endDate, columnPrefix);
    case 'monthly':
    default:
      return createMonthlyColumns(startDate, endDate, columnPrefix);
  }
}
